import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { Field, PhoneInput } from '@billda/design-system';

export interface RHFPhoneInputProps<T extends FieldValues> {
  /** Field name - must match form schema */
  name: FieldPath<T>;
  /** React Hook Form control object */
  control: Control<T>;
  /** Field label */
  label?: string;
  /** Helper text to display below the input */
  helperText?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Default country code (ISO 3166-1 alpha-2) */
  defaultCountry?: 'GB';
  /** Full width of parent container */
  fullWidth?: boolean;
  /** Data test ID for testing */
  'data-testid'?: string;
}

/**
 * Phone input integrated with React Hook Form (DS fork of shared-ui
 * RHFPhoneInput). Uses the design-system PhoneInput molecule; defaults to
 * UK (+44) format. Stores the unmasked value.
 *
 * @example
 * ```tsx
 * <RHFPhoneInput
 *   name="primaryPhoneNumber"
 *   control={form.control}
 *   label="Phone Number"
 *   required
 * />
 * ```
 */
export const RHFPhoneInput = <T extends FieldValues>({
  name,
  control,
  label,
  helperText,
  disabled,
  required,
  defaultCountry = 'GB',
  fullWidth,
  'data-testid': testId,
}: RHFPhoneInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { ref, value, onChange, onBlur },
        fieldState: { error },
      }) => (
        <Field
          label={label}
          error={error?.message}
          hint={helperText}
          required={required}
          className={fullWidth ? 'w-full' : 'w-fit'}
        >
          <PhoneInput
            value={value ?? ''}
            onChange={onChange}
            onBlur={onBlur}
            ref={ref}
            disabled={disabled}
            defaultCountry={defaultCountry}
            invalid={!!error}
            data-testid={testId}
          />
        </Field>
      )}
    />
  );
};
